import { ArrowUpRight, BellRing } from "lucide-react";
import { usd, money, duration } from "../../lib/dash";

const Card = ({ id, kicker, value, lines, tone, alert, go, tab, index }) => (
  <button
    type="button"
    data-testid={`overview-${id}`}
    onClick={() => go(tab)}
    style={{ animationDelay: `${index * 40}ms` }}
    className={`dash-rise group relative flex min-h-0 flex-col overflow-hidden rounded-2xl border bg-card p-4 text-left transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-md ${
      tone === "red" ? "border-rose-300" : tone === "amber" ? "border-amber-300" : "border-border hover:border-primary"}`}
  >
    <span className="flex items-center justify-between font-mono2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {kicker}
      <ArrowUpRight className="h-3.5 w-3.5 opacity-40 transition-opacity group-hover:opacity-100" />
    </span>
    <span data-testid={`overview-${id}-value`} className={`mt-1 font-heading text-[26px] font-bold leading-tight tabular-nums xl:text-[32px] ${tone === "red" && !alert ? "text-rose-600" : ""}`}>
      {value}
    </span>
    {alert && <span data-testid={`overview-${id}-alert`} className="text-[12px] font-semibold text-rose-600">{alert}</span>}
    {lines.filter(Boolean).map((l) => <span key={l} className="truncate text-[12px] text-muted-foreground">{l}</span>)}
  </button>
);

const printerValue = (printers) => (printers.every((p) => p.status === "unknown") ? "Not set"
  : printers.map((p) => (p.status === "unknown" ? "–" : p.status[0].toUpperCase())).join(" "));

const cards = (o) => {
  const { ai_costs: ai, sales } = o.money;
  const { ducks, orders, printers, held } = o.operations;
  const { website: w, github: gh } = o.system;
  const busy = Object.values(ducks.counts).reduce((a, b) => a + b, 0);
  return [
    ["ai", "AI costs · today", usd(ai.today_usd), [ai.budget.paused ? null : `${ai.budget.used_pct ?? 0}% of ${usd(ai.budget.today_budget_usd)} budget`, `7 days ${usd(ai.week_usd)} · month ${usd(ai.month_usd)}`], "money", o.badges.money, ai.budget.paused ? "Spend gate paused" : null],
    ["sales", `Sales · ${sales.label}`, money(sales.revenue, sales.currency), [`${sales.orders} orders · ${sales.ducks} ducks`, `${sales.images.count} images generated`], "money"],
    ["website", "Website", w.errors_15m ? `${w.errors_15m} errors` : "Healthy", [`Up ${duration(w.uptime_seconds)} · ${w.restarts_7d} restarts (7 d)`, `Heavy worker ${w.heavy_busy ? "busy" : "idle"}`], "system", w.errors_15m ? "red" : null],
    ["github", "GitHub", gh.connected ? (gh.match ? "In sync" : gh.checking ? "Checking" : "Differs") : "Not connected", [gh.connected ? gh.commit?.message : "Add a read-only token to compare", gh.connected ? gh.repo : null], "system"],
    ["ducks", "Ducks in process", busy, [`${ducks.stuck.length} stuck (${ducks.stuck_minutes}+ min)`, `${ducks.counts.sculpt} sculpting · ${ducks.counts.waiting} waiting`], "operations", ducks.stuck.length ? "red" : null],
    ["orders", "Orders · to print", orders.to_print, [`${orders.printing} printing · ${orders.open} open`, `${orders.shipped_30d} shipped (30 days)`], "operations", orders.to_print ? "amber" : null],
    ["printers", "Printers · manual", printerValue(printers), [printers.map((p) => `${p.id} ${p.status === "unknown" ? "not set" : p.status}`).join(" · "), "Not connected"], "operations"],
    ["held", "Held ducks", held.held_now, [`${held.waiting_for_review} waiting for review`, `${held.counts?.overridden ?? 0} let through so far`], "operations", held.held_now ? "red" : null],
    ["logs", "Logs · last 60 min", `${w.errors_60m} 5xx`, [`${w.log_writer?.written ?? 0} warning lines stored`, `${w.log_writer?.dropped ?? 0} dropped`], "system"],
  ];
};

const AlertsStrip = ({ a, go }) => (
  <button
    type="button"
    data-testid="overview-alerts"
    onClick={() => go("alerts")}
    className={`dash-rise group flex min-w-0 items-center gap-3 rounded-2xl border bg-card px-4 py-2.5 text-left transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-md sm:col-span-2 lg:col-span-3 ${
      a.worst === "red" ? "border-rose-300" : a.worst === "amber" ? "border-amber-300" : "border-border hover:border-primary"}`}
  >
    <BellRing className="h-4 w-4 shrink-0 text-muted-foreground" />
    <span className="shrink-0 font-mono2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Alerts</span>
    <span data-testid="overview-alerts-value" className={`shrink-0 font-heading text-lg font-bold leading-tight ${a.worst === "red" ? "text-rose-600" : a.worst === "amber" ? "text-amber-600" : ""}`}>
      {a.open ? `${a.open} open` : "All clear"}
    </span>
    <span data-testid="overview-alerts-backup" className={`ml-auto min-w-0 truncate text-[12px] ${a.backup.tone === "red" ? "font-semibold text-rose-600" : "text-amber-700"}`}>
      {a.backup.text}
    </span>
    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 opacity-40 transition-opacity group-hover:opacity-100" />
  </button>
);

export const OverviewTab = ({ o, go }) => (
  <div data-testid="dash-overview" className="grid gap-3 sm:grid-cols-2 lg:h-full lg:grid-cols-3 lg:grid-rows-[auto_repeat(3,minmax(0,1fr))]">
    <AlertsStrip a={o.alerts} go={go} />
    {cards(o).map(([id, kicker, value, lines, tab, tone, alert], i) => (
      <Card key={id} id={id} kicker={kicker} value={value} lines={lines} tab={tab} tone={tone} alert={alert} go={go} index={i} />
    ))}
  </div>
);
